import { Box, Grid } from "@mui/material";
import { useDeepCompareMemo } from "@react-hookz/web";
import { type FC, type ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { usePermissions } from "../../../../server/auth/use-permissions";
import type { UUID } from "../../../../utils/uuid";
import { useAccountLimitsContext } from "../../../context/account-limit-context";
import {
  MomentAccordion,
  type MomentAccordionProps,
} from "../../../generic/components/moment-accordion/moment-accordion";
import { ChannelForm } from "../../../segment/components/to-forms/channel-form";
import { EmployeeForm } from "../../../segment/components/to-forms/employee-form";
import { NotifyForm } from "../../../segment/components/to-forms/notify-form";
import { ToVertical } from "../../../to/components/to-vertical";
import { useToTarget } from "../../../to/use-to-target";
import type { ConnectionCategory } from "../../hooks/use-connections";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { SegmentRecipientPreview } from "../segment-recipient-preview";

interface SegmentAccordionProps extends Pick<
  MomentAccordionProps,
  "open" | "setOpen" | "onConfirm" | "sx"
> {
  formDisabled: boolean;
  lastFormAction?: "draft" | "publish";
  connectionCategories: ConnectionCategory[];
  restrictedSegments: UUID[];
  isNewsletter?: boolean;
}

export const SegmentAccordion: FC<SegmentAccordionProps> = ({
  open,
  setOpen,
  formDisabled,
  onConfirm,
  lastFormAction,
  sx,
  isNewsletter,
  connectionCategories,
  restrictedSegments,
}) => {
  const { ce_admin } = usePermissions();
  const { enable_cc_bcc } = useAccountLimitsContext();
  const [icon, setIcon] = useState<ReactElement>();
  const { toTarget } = useToTarget();
  const {
    control,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext<MomentFormMoment>();

  useWatch({
    control,
    name: ["channel", "target_channel", "immediate", "schedule.event"],
  });
  const [selectedChannel, immediate, calendar, existingCCBccs] = getValues([
    "channel",
    "immediate",
    "schedule.event.calendar",
    "cc_bcc",
  ]);

  const onSubmit = useCallback(async (): Promise<void> => {
    await trigger("segment");
    await trigger("additional_recipients");
    void onConfirm?.()?.catch(console.error);
  }, [onConfirm, trigger]);

  const employeeTarget = ["employee", "manager", "connection"].includes(toTarget);

  const hasExistingCCBcc = useDeepCompareMemo(() => {
    const cc = existingCCBccs?.cc ?? [];
    const bcc = existingCCBccs?.bcc ?? [];
    return cc.length > 0 || bcc.length > 0;
  }, [existingCCBccs]);

  const { targetEnableCCForm, showEditCC, showViewCC } = useMemo(() => {
    const enableCCForm = employeeTarget && !!selectedChannel?.includes("email");

    return {
      // Only ever applicable for emails and employee (non channel) targets.
      targetEnableCCForm: enableCCForm,
      // Editable for CE admins, flagged customers, or scheduled Communications.
      showEditCC: enableCCForm && (ce_admin || enable_cc_bcc || (!immediate && !calendar)),
      // Existing static and dynamic values remain visible regardless of access.
      showViewCC: enableCCForm && hasExistingCCBcc,
    };
  }, [
    employeeTarget,
    selectedChannel,
    ce_admin,
    enable_cc_bcc,
    immediate,
    calendar,
    hasExistingCCBcc,
  ]);

  // Clear the values if the moment itself blocks them
  useEffect(() => {
    if (!targetEnableCCForm) setValue("cc_bcc", { cc: [], bcc: [] });
  }, [targetEnableCCForm, setValue]);

  return (
    <MomentAccordion
      id="toAccordion"
      summaryIcon={icon}
      title="To"
      subtitle="Who do you want to send to?"
      index={2}
      open={open}
      setOpen={setOpen}
      disabled={formDisabled}
      sx={{ minWidth: "100%", mb: 3, ...sx }}
      onConfirm={onSubmit}
      valid={{ lastFormAction, valid: !errors.segment && !errors.additional_recipients }}
    >
      <Grid container spacing={7}>
        <Grid size={{ md: 4 }}>
          <h2>To</h2>
          Choose who should receive your Communication. You can narrow your recipients by building a
          segment or selecting an existing one.
        </Grid>
        <Grid size={{ md: 8 }}>
          <ToVertical
            disabled={formDisabled}
            enableChannels={Boolean(
              selectedChannel && ["slack", "teams"].includes(selectedChannel),
            )}
            setIcon={setIcon}
            selectedChannel={selectedChannel}
            isNewsletter={isNewsletter}
          />
          {employeeTarget && (
            <EmployeeForm
              formDisabled={formDisabled}
              continueClicked={open === "confirmed"}
              showEditCC={showEditCC}
              showViewCC={showViewCC}
              connectionCategories={connectionCategories}
              restrictedSegments={restrictedSegments}
            />
          )}

          {!isNewsletter && toTarget === "notify" && (
            <NotifyForm
              formDisabled={formDisabled}
              restrictedSegments={restrictedSegments}
              continueClicked={open === "confirmed"}
            />
          )}

          {!isNewsletter && toTarget === "channel" && (
            <ChannelForm
              formDisabled={formDisabled}
              restrictedSegments={restrictedSegments}
              continueClicked={open === "confirmed"}
            />
          )}
        </Grid>
      </Grid>
      <Grid container justifyContent="flex-end">
        <Box sx={{ mt: 3 }}>
          <SegmentRecipientPreview />
        </Box>
      </Grid>
    </MomentAccordion>
  );
};
